import React from 'react';
import { Button } from '../../../components/Button';
import type { E2eRunLast } from '../../../services/e2e-runner/e2e-runner.service';
import type { E2eRunnerController } from '../useE2eRunnerController';
import classes from '../tenants-page.module.scss';

type E2eRunControlsProps = {
  tenantId: number;
  e2eRunner: E2eRunnerController;
};

const formatElapsed = (startedAt: string | null): string => {
  if (!startedAt) {
    return '';
  }
  const minutes = Math.max(0, Math.floor((Date.now() - new Date(startedAt).getTime()) / 60_000));
  return ` · ${minutes} דק'`;
};

const formatLastResult = (last: E2eRunLast): string => {
  const outcome = last.exitCode === 0 ? 'הצליחה' : 'נכשלה';
  const when = last.stoppedAt ? ` · ${new Date(last.stoppedAt).toLocaleString('he-IL')}` : '';
  return `ריצה אחרונה: ${outcome}${when}`;
};

export const E2eRunControls: React.FC<E2eRunControlsProps> = ({ tenantId, e2eRunner }) => {
  const { status, isBusy, error, statusError, requestLaunch, stopRun } = e2eRunner;

  const statusErrorLine = statusError ? (
    <span className={classes.e2eRunError} data-testid={`e2e-run-status-error-${tenantId}`}>
      טעינת מצב ריצת הבדיקות נכשלה
    </span>
  ) : null;

  if (!status?.enabled) {
    return statusErrorLine;
  }

  const isRunning = status.status === 'running';

  return (
    <div className={classes.e2eRunSlot} data-testid={`e2e-run-status-${tenantId}`}>
      <div className={classes.e2eRunLabels}>
        <span className={classes.e2eRunBranch}>
          {`ריצת בדיקות · ${status.branch ?? '—'}${isRunning ? formatElapsed(status.active?.startedAt ?? null) : ''}`}
        </span>
        {!isRunning && status.lastResult && (
          <span className={classes.e2eRunBranch} data-testid={`e2e-run-last-${tenantId}`}>
            {formatLastResult(status.lastResult)}
          </span>
        )}
        {statusErrorLine}
      </div>
      {isRunning ? (
        <Button variant="cancel" onClick={stopRun} disabled={isBusy} dataTestId={`e2e-run-stop-${tenantId}`}>
          עצור
        </Button>
      ) : (
        <>
          <Button variant="save" onClick={() => requestLaunch('gate')} disabled={isBusy} dataTestId={`e2e-run-gate-${tenantId}`}>
            gate
          </Button>
          <Button variant="save" onClick={() => requestLaunch('full')} disabled={isBusy} dataTestId={`e2e-run-full-${tenantId}`}>
            full
          </Button>
        </>
      )}
      {error && <span className={classes.e2eRunError}>{error}</span>}
    </div>
  );
};
